import type { CanalComercial } from './statusReserva'

export type StatusCampanha = 'RASCUNHO' | 'ATIVA' | 'ENCERRADA'

/** Janela de apuração da meta (V53, VC-02a): vigência inteira, mês civil ou ano civil. */
export type PeriodicidadeMeta = 'PERIODO' | 'MENSAL' | 'ANUAL'

export const PERIODICIDADE_ROTULO: Record<PeriodicidadeMeta, string> = {
  PERIODO: 'Vigência inteira',
  MENSAL: 'Mensal',
  ANUAL: 'Anual',
}

/**
 * VOLUME = valor estimado das reservas ganhas (data de assinatura); RECEITA = receita Originei
 * confirmada pelo Financeiro, por competência (V54, AN-7). Só muda em RASCUNHO (422 fora dele).
 */
export type TipoMeta = 'VOLUME' | 'RECEITA'

export interface Campanha {
  id: string
  nome: string
  descricao?: string | null
  /**
   * Meta coletiva do período corrente. Opcional desde a AN-8 (Q31): `null` = campanha sem meta
   * coletiva — a tela mostra "sem meta coletiva", nunca 0%.
   */
  valorMeta: number | null
  dataInicio: string // ISO date (YYYY-MM-DD)
  dataFim: string
  produtoNome?: string | null
  consultorId?: string | null
  status: StatusCampanha
  valorFechado: number
  /** null sem meta coletiva (Q31). */
  progressoPercent: number | null
  oportunidadesContratadas: number
  /** Consultores inscritos (V43, A14). */
  participantes: number
  /** Produção Ganha na janela corrente — mesma regra de /producao. */
  producaoGanha: number
  /** producaoGanha ÷ valorMeta em %, sem teto; null sem meta coletiva (Q31). */
  atingimentoPercent: number | null
  createdAt: string
  updatedAt: string
  // ── V53 (VC-02a/b) — opcionais: o backend antigo não os envia ──
  periodicidade?: PeriodicidadeMeta
  tipoMeta?: TipoMeta
  /** Meta individual padrão de cada participante. */
  valorMetaParticipante?: number | null
  /** Informativa — nunca gera pagamento. */
  premiacao?: string | null
  /** Documento tipoEntidade=CAMPANHA no módulo documentos. */
  arteDocumentoId?: string | null
  /** Janela corrente de apuração (calculada no backend). */
  janelaInicio?: string | null
  janelaFim?: string | null
  rotuloPeriodo?: string | null
  // ── AN-8 (V56) — audiência materializada; opcionais para o backend antigo ──
  audienceType?: AudienceType
  audienciaCanais?: CanalComercial[] | null
  audienciaEmpresas?: string[] | null
  audienciaDefinidaPor?: string | null
  audienciaDefinidaEm?: string | null
  audienciaMaterializadaEm?: string | null
  /** Inscritos da equipe do GERENTE+GESTOR_EQUIPE; null para os demais papéis. */
  participantesEquipe?: number | null
}

// ----- Audiência (AN-8 §3) -----

/**
 * Quem participa da campanha. TODOS_ATIVOS = todos os consultores ativos da plataforma
 * (Q-AN8-1); CANAIS/EMPRESAS = dinâmicas (materializadas pelo backend); NOMINAL = lista manual.
 */
export type AudienceType = 'TODOS_ATIVOS' | 'CANAIS' | 'EMPRESAS' | 'NOMINAL'

export const AUDIENCIA_ROTULO: Record<AudienceType, string> = {
  TODOS_ATIVOS: 'Todos os consultores ativos',
  CANAIS: 'Por canal',
  EMPRESAS: 'Por empresa',
  NOMINAL: 'Lista nominal',
}

/** Audiência dinâmica: inscrever/remover à mão é recusado (422 CAMPANHA_AUDIENCIA_DINAMICA). */
export const audienciaDinamica = (t?: AudienceType | null): boolean => !!t && t !== 'NOMINAL'

/** Origem da inscrição (V56). */
export type OrigemParticipante = 'MANUAL' | 'AUDIENCIA' | 'MIGRACAO'

export const ORIGEM_ROTULO: Record<OrigemParticipante, string> = {
  MANUAL: 'Manual',
  AUDIENCIA: 'Audiência',
  MIGRACAO: 'Migração',
}

/**
 * Texto de apoio para os códigos de erro da AN-8, usado só quando o backend não manda
 * `message` (o texto do backend tem precedência — `mensagemErroCampanha`).
 */
export const ERRO_CAMPANHA_ROTULO: Record<string, string> = {
  CAMPANHA_AUDIENCIA_OBRIGATORIA: 'Escolha quem participa da campanha',
  CAMPANHA_AUDIENCIA_INVALIDA: 'Audiência inválida: escolha ao menos um canal ou uma empresa',
  CAMPANHA_SEM_OBJETIVO: 'Informe ao menos uma meta, a descrição ou a premiação',
  CAMPANHA_CONSULTOR_ID_DESCONTINUADO: 'Campanha individual foi descontinuada; use a lista nominal',
  CAMPANHA_META_INVALIDA: 'A meta coletiva deve ser positiva',
  CAMPANHA_AUDIENCIA_VAZIA: 'A audiência não tem nenhum consultor ativo — a campanha não pode ser ativada',
  CAMPANHA_AUDIENCIA_IMUTAVEL: 'Com a campanha ativa a audiência só pode ser ampliada; encerrada, não muda',
  CAMPANHA_AUDIENCIA_DINAMICA: 'Audiência dinâmica: a inscrição é automática',
  CAMPANHA_EM_USO_NO_RANKING: 'A campanha está em uso na configuração do ranking',
  CONSULTOR_INVALIDO: 'Consultor inválido ou inativo',
  PARTICIPANTE_DUPLICADO: 'Este consultor já participa da campanha',
  FORA_DA_EQUIPE: 'Consultor fora da sua equipe',
}

/** Mensagem do backend; sem ela, o rótulo do código; sem ambos, o padrão da tela. */
export const mensagemErroCampanha = (e: unknown, padrao: string): string => {
  const env = e as { message?: string; data?: { errorCode?: string; message?: string } } | null
  // O interceptor de `api` já põe a mensagem do backend (com a ref. do correlationId) em `message`.
  const msg = env?.message ?? env?.data?.message
  if (msg) return msg
  const codigo = env?.data?.errorCode
  return (codigo && ERRO_CAMPANHA_ROTULO[codigo]) || padrao
}

// ----- Trilha de alterações (AN-8 §4) -----

export type TipoAlteracaoGestao =
  | 'AUDIENCIA'
  | 'AUDIENCIA_MATERIALIZADA'
  | 'META_COLETIVA'
  | 'META_PADRAO'
  | 'STATUS'
  | 'PARTICIPANTE_INSCRITO'
  | 'PARTICIPANTE_REMOVIDO'
  | 'META_INDIVIDUAL'
  | 'EQUIPE_CRIADA'
  | 'EQUIPE_NOME'
  | 'EQUIPE_GERENTE'
  | 'EQUIPE_DESATIVADA'
  | 'EQUIPE_MEMBRO_INCLUIDO'
  | 'EQUIPE_MEMBRO_REMOVIDO'
  | 'RANKING_ESCOPO'

export const ALTERACAO_ROTULO: Record<TipoAlteracaoGestao, string> = {
  AUDIENCIA: 'Audiência alterada',
  AUDIENCIA_MATERIALIZADA: 'Audiência materializada',
  META_COLETIVA: 'Meta coletiva alterada',
  META_PADRAO: 'Meta padrão por consultor alterada',
  STATUS: 'Status alterado',
  PARTICIPANTE_INSCRITO: 'Participante inscrito',
  PARTICIPANTE_REMOVIDO: 'Participante removido',
  META_INDIVIDUAL: 'Meta individual alterada',
  EQUIPE_CRIADA: 'Equipe criada',
  EQUIPE_NOME: 'Nome da equipe alterado',
  EQUIPE_GERENTE: 'Gerente da equipe trocado',
  EQUIPE_DESATIVADA: 'Equipe desativada',
  EQUIPE_MEMBRO_INCLUIDO: 'Membro incluído',
  EQUIPE_MEMBRO_REMOVIDO: 'Membro removido',
  RANKING_ESCOPO: 'Escopo do ranking alterado',
}

/** Rótulo legível; tipo desconhecido (backend mais novo) aparece cru, nunca some. */
export const rotuloAlteracao = (tipo: string): string =>
  ALTERACAO_ROTULO[tipo as TipoAlteracaoGestao] ?? tipo

export const AUTOR_PAPEL_ROTULO: Record<string, string> = {
  ADMIN: 'Administrador',
  GERENTE: 'Gerente',
  SISTEMA: 'Sistema',
}

/** `GET /v1/campanhas/{id}/alteracoes` e `/v1/equipes/{id}/alteracoes` (append-only). */
export interface AlteracaoGestao {
  id: string
  entidade: string
  entidadeId: string
  consultorId?: string | null
  tipo: string
  valorAnterior?: string | null
  valorNovo?: string | null
  autorId: string
  autorPapel: string
  criadoEm: string
}

/** `GET /v1/campanhas/{id}/consultores-elegiveis?busca=` (máx. 200, só não inscritos). */
export interface ConsultorElegivel {
  consultorId: string
  nome?: string | null
  email?: string | null
  canal?: string | null
  tenantId?: string | null
}

/** `POST /v1/campanhas/audiencia/sincronizar` (ADMIN). */
export interface SincronizacaoAudiencia {
  campanhas: number
  inscritos: number
}

// ----- Participantes e produção (Onda 7.5 / A14) -----

export interface CampanhaParticipante {
  id: string
  campanhaId: string
  consultorId: string
  tenantId?: string | null
  canal?: string | null
  inscritoEm: string
  inscritoPor: string
  /** Meta individual gravada na inscrição (V53); nula = sem meta própria. */
  valorMeta?: number | null
  /** Meta que vale para o consultor (individual → padrão da campanha); nula = não definida. */
  metaEfetiva?: number | null
  /** AN-8: como entrou (manual, pela audiência ou migração da V56). */
  origem?: OrigemParticipante
  /** AN-8: nome da projeção de usuários (back-office). */
  nome?: string | null
}

/**
 * AN-8: tenant/canal são carimbados pelo backend a partir da projeção (o corpo é ignorado) —
 * a tela manda só o consultor e a meta.
 */
export interface CampanhaParticipanteRequest {
  consultorId: string
  valorMeta?: number | null
}

export interface ProducaoParticipante {
  posicao: number
  consultorId: string
  canal?: string | null
  producao: number
  quantidade: number
  /** Produção ÷ meta coletiva (contribuição para a campanha); null sem meta coletiva. */
  atingimentoPercent: number | null
  /** Meta individual efetiva (V53); nula = não definida. */
  metaIndividual?: number | null
  /** Produção ÷ meta individual, sem teto; nula sem meta individual. */
  atingimentoIndividualPercent?: number | null
  /** AN-8: nome da projeção de usuários. */
  nome?: string | null
}

export interface ProducaoCanal {
  canal: string
  participantes: number
  producao: number
  quantidade: number
  participacaoPercent: number
}

export interface CampanhaProducao {
  campanhaId: string
  /** null sem meta coletiva (Q31). */
  valorMeta: number | null
  producaoTotal: number
  atingimentoPercent: number | null
  participantes: number
  /** Para o GERENTE+GESTOR: só a equipe, com a posição da campanha inteira. */
  ranking: ProducaoParticipante[]
  porCanal: ProducaoCanal[]
  /** Só GERENTE+GESTOR_EQUIPE; null para o ADMIN. */
  producaoEquipe?: number | null
  participantesEquipe?: number | null
}

export interface CampanhaRequest {
  nome: string
  descricao?: string | null
  /** Opcional (Q31): ao menos um de meta coletiva, meta por consultor, descrição ou premiação. */
  valorMeta?: number | null
  dataInicio: string
  dataFim: string
  produtoNome?: string | null
  // `consultorId` descontinuado (AN-8): a API recusa valor não nulo
  // (400 CAMPANHA_CONSULTOR_ID_DESCONTINUADO) — a tela nunca envia.
  status?: StatusCampanha
  // ── V53 ── (PUT substitui tudo: omitir volta ao default/nulo)
  periodicidade?: PeriodicidadeMeta
  tipoMeta?: TipoMeta
  valorMetaParticipante?: number | null
  premiacao?: string | null
  arteDocumentoId?: string | null
  // ── AN-8 ──
  audienceType: AudienceType
  audienciaCanais?: CanalComercial[]
  audienciaEmpresas?: string[]
}
